import { useEffect, type JSX } from 'react';
import { useT } from '../i18n/index.js';
import { cancelTalk, startTalk, stopTalk, useVoice } from '../sound/voice.js';
import { Icon } from './Icon.js';
import './VoiceTalk.css';

/**
 * Hold-to-talk. Pointer capture keeps the release on the button however the
 * thumb slides — and every way a release can stop being deliverable (the
 * button unmounts, disables mid-hold, or loses keyboard focus) cancels the
 * recording outright, because audio captured after a release must never send.
 * A press that was refused the mic parks the button disabled with the reason
 * in its tooltip rather than failing silently on every hold.
 */
export function VoiceTalk({ tableId, disabled = false }: { tableId: string; disabled?: boolean }): JSX.Element {
  const t = useT();
  const ptt = useVoice((s) => s.ptt);
  const denied = useVoice((s) => s.denied);
  const live = ptt === 'live';

  useEffect(() => () => cancelTalk(), []);
  useEffect(() => {
    if (disabled || denied) cancelTalk();
  }, [disabled, denied]);

  return (
    <button
      type="button"
      className="ptt"
      data-live={live}
      disabled={disabled || denied}
      aria-pressed={live}
      aria-label={t('ui.voice.hold')}
      title={denied ? t('ui.voice.denied') : t('ui.voice.hold')}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        void startTalk(tableId);
      }}
      onPointerUp={stopTalk}
      onPointerCancel={cancelTalk}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) void startTalk(tableId);
      }}
      onKeyUp={(e) => {
        if (e.key === ' ' || e.key === 'Enter') stopTalk();
      }}
      onBlur={cancelTalk}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Icon name="mic" size={13} />
    </button>
  );
}
